/**
 * NCComponent — Classe de base pour les Web Components National Conteneur.
 *
 * Fournit :
 *  - AbortController automatiquement géré (abort au disconnectedCallback)
 *  - fetchWith(url, options) — fetch avec signal, annule la requête précédente
 *  - emit(name, detail) — dispatch CustomEvent { bubbles: true, composed: true }
 *  - $(selector) / $$(selector) — helpers querySelector scoped au composant
 *
 * Usage :
 *   class NcMonComposant extends NCComponent {
 *     connectedCallback() {
 *       super.connectedCallback?.();
 *       this.$('.btn')?.addEventListener('click', () => this.#handleClick());
 *     }
 *     async #handleClick() {
 *       const res = await this.fetchWith('/api/data.json');
 *       if (!res.ok) return;
 *       this.emit('nc:data-loaded', await res.json());
 *     }
 *   }
 *   customElements.define('nc-mon-composant', NcMonComposant);
 */

if (!window.NCComponent) {
  window.NCComponent = class NCComponent extends HTMLElement {
    /** @type {AbortController|null} */
    #ctrl = null;

    // ── Sélection ────────────────────────────────────────────────────────────

    /**
     * querySelector scopé au composant.
     * @param {string} selector
     * @returns {Element|null}
     */
    $(selector) {
      return this.querySelector(selector);
    }

    /**
     * querySelectorAll scopé au composant, retourne un Array.
     * @param {string} selector
     * @returns {Element[]}
     */
    $$(selector) {
      return Array.from(this.querySelectorAll(selector));
    }

    // ── Réseau ───────────────────────────────────────────────────────────────

    /**
     * Lance un fetch lié à ce composant.
     * Annule automatiquement toute requête précédente en cours.
     *
     * @param {string|URL} url
     * @param {RequestInit} [options={}]
     * @returns {Promise<Response>}
     * @throws {Error} — sauf AbortError (ignoré silencieusement)
     */
    async fetchWith(url, options = {}) {
      this.#ctrl?.abort();
      this.#ctrl = new AbortController();
      try {
        return await fetch(url, { ...options, signal: this.#ctrl.signal });
      } catch (err) {
        if (err.name === 'AbortError') return null;
        throw err;
      }
    }

    /**
     * Annule la requête en cours si active, réinitialise le contrôleur.
     */
    abort() {
      this.#ctrl?.abort();
      this.#ctrl = null;
    }

    // ── Événements ───────────────────────────────────────────────────────────

    /**
     * Dispatch un CustomEvent depuis ce composant.
     * L'événement remonte (bubbles) et traverse le shadow DOM (composed).
     *
     * @param {string} name — Nom de l'événement (convention : 'nc:action')
     * @param {*} [detail]  — Données arbitraires
     */
    emit(name, detail) {
      this.dispatchEvent(
        new CustomEvent(name, { detail, bubbles: true, composed: true })
      );
    }

    // ── Cycle de vie ─────────────────────────────────────────────────────────

    /**
     * Annule automatiquement les requêtes en cours à la déconnexion.
     * Les sous-classes DOIVENT appeler super.disconnectedCallback() si elles
     * surchargent cette méthode.
     */
    disconnectedCallback() {
      this.abort();
    }
  };
}

if (!window.NCA11y) {
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const activeTraps = new WeakMap();
  const describedByMap = new WeakMap();

  const isVisible = el => {
    if (!el) return false;
    if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
    if (el.closest('[hidden], [inert]')) return false;
    return true;
  };

  const getFocusable = container => {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
  };

  const focusFirst = (container, preferredSelector) => {
    const preferred = preferredSelector ? container?.querySelector(preferredSelector) : null;
    if (preferred && isVisible(preferred) && typeof preferred.focus === 'function') {
      preferred.focus();
      return preferred;
    }
    const first = getFocusable(container)[0];
    first?.focus();
    return first || null;
  };

  const trapFocus = (container, options = {}) => {
    if (!container) return () => {};
    activeTraps.get(container)?.();

    const handler = event => {
      if (event.key === 'Escape' && typeof options.onEscape === 'function') {
        event.preventDefault();
        options.onEscape(event);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusable(container);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handler);
    const release = () => {
      container.removeEventListener('keydown', handler);
      activeTraps.delete(container);
    };
    activeTraps.set(container, release);
    return release;
  };

  const openDialog = (dialog, options = {}) => {
    if (!dialog) return;
    const trigger = options.trigger || document.activeElement;
    dialog.__ncReturnFocus = trigger instanceof HTMLElement ? trigger : null;
    if (options.modal !== false) dialog.setAttribute('aria-modal', 'true');
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else dialog.setAttribute('open', '');

    trapFocus(dialog, {
      onEscape: () => closeDialog(dialog)
    });
    window.requestAnimationFrame(() => focusFirst(dialog, options.initialFocus));
  };

  const closeDialog = (dialog, options = {}) => {
    if (!dialog) return;
    activeTraps.get(dialog)?.();
    dialog.removeAttribute('aria-modal');
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    if (options.returnFocus !== false) dialog.__ncReturnFocus?.focus?.();
  };

  const appendDescribedBy = (field, id) => {
    if (!field || !id) return;
    const previous = field.getAttribute('aria-describedby') || '';
    if (!describedByMap.has(field)) describedByMap.set(field, previous);
    const ids = previous.split(/\s+/).filter(Boolean);
    if (!ids.includes(id)) ids.push(id);
    field.setAttribute('aria-describedby', ids.join(' '));
  };

  const restoreDescribedBy = field => {
    if (!field || !describedByMap.has(field)) return;
    const previous = describedByMap.get(field);
    if (previous) field.setAttribute('aria-describedby', previous);
    else field.removeAttribute('aria-describedby');
    describedByMap.delete(field);
  };

  const setFieldError = (field, errorEl, message) => {
    if (!field || !errorEl) return;
    if (!errorEl.id) errorEl.id = field.id ? field.id + '-error' : 'nc-field-error-' + Math.random().toString(36).slice(2);
    if (message) errorEl.textContent = message;
    errorEl.hidden = false;
    errorEl.setAttribute('role', 'alert');
    field.classList.add('is-error');
    field.setAttribute('aria-invalid', 'true');
    appendDescribedBy(field, errorEl.id);
  };

  const clearFieldError = (field, errorEl) => {
    if (!field) return;
    field.classList.remove('is-error');
    field.removeAttribute('aria-invalid');
    restoreDescribedBy(field);
    if (errorEl) errorEl.hidden = true;
  };

  const validateField = (field, errorEl, messages = {}) => {
    if (!field) return true;
    const value = field.value.trim();
    let message = '';
    const _TF = (window.NCI18n && window.NCI18n.form) || {};
    if (field.required && !value) message = messages.required || _TF.err_required || 'Ce champ est obligatoire.';
    else if (field.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) message = messages.email || _TF.err_email_format || 'Veuillez saisir une adresse email valide.';
    else if (field.type === 'tel' && value && value.replace(/[\s\-.()]/g, '').length < 8) message = messages.tel || _TF.err_phone_format || 'Veuillez saisir un numéro de téléphone valide.';

    if (message) {
      setFieldError(field, errorEl, message);
      return false;
    }
    clearFieldError(field, errorEl);
    return true;
  };

  window.NCA11y = {
    FOCUSABLE_SELECTOR,
    getFocusable,
    focusFirst,
    trapFocus,
    openDialog,
    closeDialog,
    setFieldError,
    clearFieldError,
    validateField
  };
}
